import type { Picture } from "@/components/ResponsiveImg";
import workThunderbolt from "@/assets/work-thunderbolt.webp?w=480;800;1280&format=webp&as=picture";
import workRockland from "@/assets/work-rockland.webp?w=480;800;1280&format=webp&as=picture";
import techthingsEpisodes from "@/assets/work/techthings-episodes.webp?w=480;800;1280&format=webp&as=picture";
import cudoShortform from "@/assets/work/cudo-shortform.webp?w=480;800;1280&format=webp&as=picture";
import cudoNewsfeed from "@/assets/work/cudo-newsfeed.webp?w=480;800;1280&format=webp&as=picture";
import cudoEventGtc from "@/assets/work/cudo-event-gtc.webp?w=480;800;1280&format=webp&as=picture";
import cudoEventSc25 from "@/assets/work/cudo-event-sc25.webp?w=480;800;1280&format=webp&as=picture";
import cudoNvidiaFeature from "@/assets/work/cudo-nvidia-feature.webp?w=480;800;1280&format=webp&as=picture";
import waypointSnackstack from "@/assets/work/waypoint-snackstack.webp?w=480;800;1280&format=webp&as=picture";
import waypointCommunity from "@/assets/work/waypoint-community.webp?w=480;800;1280&format=webp&as=picture";
import thunderboltGamer from "@/assets/work/thunderbolt-gamer.webp?w=480;800;1280&format=webp&as=picture";
import thunderboltWorker from "@/assets/work/thunderbolt-worker.webp?w=480;800;1280&format=webp&as=picture";
import logoCudo from "@/assets/logos/cudo.webp?w=128&format=webp";
import logoIntel from "@/assets/logos/intel.webp?w=128&format=webp";
import logoRockland from "@/assets/logos/rockland.webp?w=192&format=webp";
import logoWaypoint from "@/assets/logos/waypoint.webp?w=128&format=webp";
import logoTechThings from "@/assets/logos/techthings.webp?w=128&format=webp";
import logoThunderbolt from "@/assets/logos/thunderbolt.webp?w=128&format=webp";
import {
  Eye,
  MousePointerClick,
  Play,
  Users,
  Globe,
  Film,
  Linkedin,
  Youtube,
  type LucideIcon,
} from "lucide-react";

export { cudoNvidiaFeature };

export const projectImages: Record<string, Picture> = {
  cudo: cudoNvidiaFeature,
  waypoint: waypointSnackstack,
  thunderbolt: workThunderbolt,
  rockland: workRockland,
  techthings: techthingsEpisodes,
};

export const projectCoverCaptions: Record<string, string> = {
  cudo: "CUDO Compute recognized by Jensen Huang as key UK GPU cloud provider",
  waypoint: "Waypoint Snack Stack instant-win campaign creative",
  thunderbolt: "Thunderbolt Technology explainer infographic",
  rockland: "Rocklands Entertainment Inc. managed artist collage",
  techthings: "Tech & Things Official Podcast Banner",
};

export const projectImageHrefs: Record<string, string> = {
  cudo: "https://www.linkedin.com/posts/cudocompute_cudo-compute-powers-nvidia-ai-infra-in-uk-activity-7338581907791585280-m__d",
  waypoint: "https://waypointconvenience.com/snackstack/",
  thunderbolt: "https://www.thunderbolttechnology.net/",
  rockland: "https://www.rocklandsentertainment.com/",
  techthings: "https://www.youtube.com/@scottcbusiness",
};

export const projectRelatedService: Record<string, { slug: string; label: string }> = {
  cudo: { slug: "content-strategy", label: "Content strategy" },
  thunderbolt: { slug: "copywriting", label: "Copywriting" },
  waypoint: { slug: "social-media", label: "Social & community" },
  rockland: { slug: "social-media", label: "Social & community" },
  techthings: { slug: "social-media", label: "Social & community" },
};

export function cudoMetricIcon(label: string): LucideIcon {
  const l = label.toLowerCase();
  if (l.includes("total")) return Globe;
  if (l.includes("impression")) return Eye;
  if (l.includes("engagement") && l.includes("linkedin")) return Linkedin;
  if (l.includes("engagement") || l.includes("click")) return MousePointerClick;
  if (l.includes("video view")) return Play;
  if (l.includes("watch") || l.includes("youtube")) return Youtube;
  if (l.includes("follower")) return Users;
  if (l.includes("clip") || l.includes("interview")) return Film;
  return Eye;
}

export const projectGlance: Record<string, { v: string; l: string }[]> = {
  cudo: [
    { v: "3 brands", l: "CUDO Ventures · CUDO Compute · CUDOS" },
    { v: "52 events", l: "Attended 2024-2026, GTC · Web Summit · Token2049" },
    { v: "9 templates", l: "JupyterLab, vLLM, DeepSeek-R1, Llama 3.3/3.1, Dify…" },
    { v: "30+ new partners", l: "2024-2026, Conapto · Canopy Cloud · Cocoon AI · Fetch · SingularityNET" },
    { v: "NVIDIA GTC 2025", l: "Featured by Jensen Huang as a key UK GPU cloud provider, preferred NVIDIA Cloud Partner" },
  ],
  thunderbolt: [
    { v: "6 yrs", l: "Continuous program ownership across US, Canada, and global" },
    { v: "Intel + MMK", l: "Sub-feature campaigns for Intel and Intel Canada activations" },
    { v: "3 creators", l: "KittyPlays · KarQ · SuperScientific (Danny Winget)" },
    { v: "$400K paid", l: "Meta paid social end-to-end with monthly + quarterly reporting" },
  ],
  rockland: [
    { v: "9 tours", l: "Active artist campaigns across Canada and US Midwest" },
    { v: "100+ shows", l: "Frank Mills · Charley Pride · Red Green · Yesterday Once More" },
    { v: "Sold-out runs", l: "Theatres across Ontario, the Maritimes, and US Midwest" },
    { v: "Full stack", l: "Video ads, copy, regional targeting, print + digital assets" },
  ],
  waypoint: [
    { v: "200+ stores", l: "B2B + B2C content across Instagram, Facebook, LinkedIn, TikTok" },
    { v: "152,919 plays", l: "Instant Win / Snack Stack promotion mechanics" },
    { v: "+321% YoY", l: "Social impressions lift across owned channels" },
    { v: "Live coverage", l: "Trade shows, store openings, and sponsorship activations" },
  ],
  techthings: [
    { v: "550+ episodes", l: "Long-form podcast across 6 years of continuous publishing" },
    { v: "50+ platforms", l: "YouTube, X, LinkedIn, podcast networks, Web3 video" },
    { v: "100+ guests", l: "John McAfee · Andreas Antonopoulos · Naomi Brockwell · Web3 founders" },
    { v: "Self-produced", l: "Research, booking, hosting, editing, publishing, community" },
  ],
};

export const projectLogos: Record<string, string> = {
  cudo: logoCudo,
  thunderbolt: logoIntel,
  rockland: logoRockland,
  waypoint: logoWaypoint,
  techthings: logoTechThings,
};

export const projectExtraLogos: Record<string, string[]> = {
  thunderbolt: [logoThunderbolt],
};

export const projectSurfaces: Record<string, string[]> = {
  cudo: ["Content lead", "Copywriting", "Newsletter", "Sales enablement", "LinkedIn", "X", "Facebook", "YouTube", "Discord", "Video editing", "Graphic design"],
  thunderbolt: ["YouTube", "Instagram", "Facebook", "X"],
  rockland: ["Facebook", "Meta Ads"],
  waypoint: ["Instagram", "Facebook"],
  techthings: ["200+ platforms", "X", "Instagram", "Facebook", "YouTube", "LBRY / Odysee (founding creator)", "Decentralized & alt-social"],
};

export type GalleryItem = {
  src: Picture;
  alt: string;
  span?: "wide" | "tall" | "square" | "full" | "half";
  fit?: "cover" | "contain";
  href?: string;
};

export const projectGalleries: Record<string, GalleryItem[]> = {
  cudo: [
    { src: cudoShortform, alt: "CUDO short-form content showcase", span: "half", fit: "contain", href: "https://www.cudocompute.com/" },
    { src: cudoNewsfeed, alt: "CUDO newsfeed showcase", span: "half", fit: "contain", href: "https://www.cudocompute.com/" },
  ],
  waypoint: [
    { src: waypointCommunity, alt: "Waypoint community moments from campaign creative", span: "full", fit: "contain", href: "https://waypointconvenience.com/" },
  ],
  thunderbolt: [
    { src: thunderboltGamer, alt: "Thunderbolt gamer video for gaming, streaming, and creating anywhere", span: "half", fit: "contain", href: "https://youtu.be/Mp10my6Ji-E" },
    { src: thunderboltWorker, alt: "Thunderbolt worker video showing dual displays and modern desktop workflows", span: "half", fit: "contain", href: "https://youtu.be/eksaXPg8RXg" },
  ],
};

export const projectEventGalleries: Record<string, GalleryItem[]> = {
  cudo: [
    { src: cudoEventGtc, alt: "NVIDIA GTC 2025 event highlights", span: "half", fit: "contain", href: "https://www.cudocompute.com/" },
    { src: cudoEventSc25, alt: "SC25 CUDO Compute booth highlights", span: "half", fit: "contain", href: "https://www.cudocompute.com/" },
  ],
};

export const caseOgImages: Record<string, string> = {
  cudo: "https://scottcbusiness.com/og/cudo.webp",
  thunderbolt: "https://scottcbusiness.com/og/thunderbolt.webp",
  rockland: "https://scottcbusiness.com/og/rockland.webp",
  waypoint: "https://scottcbusiness.com/og/waypoint.webp",
  techthings: "https://scottcbusiness.com/og/techthings.webp",
};

export const publicToInternalSlug: Record<string, string> = {
  "cudo": "cudo",
  "thunderbolt-intel": "thunderbolt",
  "rocklands": "rockland",
  "waypoint": "waypoint",
  "tech-and-things": "techthings",
};

export const internalToPublicSlug: Record<string, string> = {
  cudo: "cudo",
  thunderbolt: "thunderbolt-intel",
  rockland: "rocklands",
  waypoint: "waypoint",
  techthings: "tech-and-things",
};

export const publicSlugs = Object.keys(publicToInternalSlug);
